import React, { useMemo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  TooltipProps,
  Cell,
} from 'recharts';
import { Home, Loader2, AlertTriangle } from 'lucide-react';

/**
 * Interface for shed management data
 */
export interface ShedData {
  shedNumber: string;
  capacity: number;
  occupancy: number;
  isActive?: boolean;
}

interface ShedManagementChartProps {
  data: ShedData[];
  loading?: boolean;
  error?: string | null;
  height?: number;
}

/**
 * Get occupancy color based on utilization percentage
 */
const getOccupancyColor = (occupancy: number, capacity: number): string => {
  const percentage = (occupancy / capacity) * 100;
  if (percentage <= 50) return '#10b981'; // green - healthy
  if (percentage <= 80) return '#f59e0b'; // amber - moderate
  return '#ef4444'; // red - crowded
};

/**
 * Get occupancy status
 */
const getOccupancyStatus = (occupancy: number, capacity: number): string => {
  const percentage = (occupancy / capacity) * 100;
  if (percentage <= 50) return 'Healthy';
  if (percentage <= 80) return 'Moderate';
  return 'Crowded';
};

/**
 * Custom tooltip for shed chart
 */
const CustomTooltip: React.FC<TooltipProps<number, string>> = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const item = payload[0].payload;
    const percentage = ((item.occupancy / item.capacity) * 100).toFixed(1);
    const status = getOccupancyStatus(item.occupancy, item.capacity);

    return (
      <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
        <p className="font-semibold text-gray-700 mb-2">{item.shedNumber}</p>
        <div className="space-y-1">
          <p className="text-sm text-gray-600">
            Occupancy: <span className="font-bold">{item.occupancy}</span> animals
          </p>
          <p className="text-sm text-gray-600">
            Capacity: <span className="font-bold">{item.capacity}</span> animals
          </p>
          <p className="text-sm text-gray-600">
            Utilization: <span className="font-bold">{percentage}%</span>
          </p>
          <p className={`text-sm font-semibold ${
            status === 'Healthy' ? 'text-green-600' :
            status === 'Moderate' ? 'text-yellow-600' : 'text-red-600'
          }`}>
            Status: {status}
          </p>
        </div>
      </div>
    );
  }
  return null;
};

/**
 * ShedManagementChart - Display shed occupancy and capacity
 * Wrapped with React.memo for performance optimization
 * Will only re-render when props actually change
 */
const ShedManagementChartContent: React.FC<ShedManagementChartProps> = ({
  data,
  loading = false,
  error = null,
  height = 500,
}) => {
  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center" style={{ height }}>
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        <span className="ml-2 text-gray-600">Loading shed data...</span>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center" style={{ height }}>
        <div className="text-center">
          <p className="text-red-600 font-semibold">Failed to load chart</p>
          <p className="text-sm text-gray-500 mt-1">{error}</p>
        </div>
      </div>
    );
  }

  // Empty state
  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center" style={{ height }}>
        <div className="text-center text-gray-500">
          <Home className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p>No shed data available</p>
        </div>
      </div>
    );
  }

  // Calculate shed statistics and prepare chart data with useMemo to ensure proper re-rendering
  const { chartData, crowdedSheds, healthySheds, totalCapacity, totalOccupancy, averageUtilization } = useMemo(() => {
    const crowded = data.filter(shed => (shed.occupancy / shed.capacity) * 100 > 80);
    const healthy = data.filter(shed => (shed.occupancy / shed.capacity) * 100 <= 50);
    const totalCap = data.reduce((sum, shed) => sum + shed.capacity, 0);
    const totalOcc = data.reduce((sum, shed) => sum + shed.occupancy, 0);
    const avgUtil = totalCap > 0 ? ((totalOcc / totalCap) * 100).toFixed(1) : '0';

    // Prepare data for chart - this is the critical part for dynamic updates
    const chart = data.map(shed => ({
      shedNumber: shed.shedNumber,
      capacity: shed.capacity,
      occupancy: shed.occupancy,
      isActive: shed.isActive,
      available: Math.max(0, shed.capacity - shed.occupancy) // Ensure available is never negative
    }));

    return {
      chartData: chart,
      crowdedSheds: crowded,
      healthySheds: healthy,
      totalCapacity: totalCap,
      totalOccupancy: totalOcc,
      averageUtilization: avgUtil
    };
  }, [data]); // Re-calculate whenever data changes

  return (
    <div className="w-full">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-800 flex items-center">
          <Home className="w-5 h-5 mr-2 text-indigo-600" />
          Shed Management & Capacity
        </h3>
        <p className="text-sm text-gray-500 mt-1">
          Occupancy levels and capacity utilization by shed
        </p>
      </div>

      {/* Alert Section */}
      {crowdedSheds.length > 0 && (
        <div className="mb-4 bg-orange-50 border-l-4 border-orange-400 p-3 rounded">
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 text-orange-600 mr-2" />
            <div>
              <p className="text-sm font-semibold text-orange-800">
                {crowdedSheds.length} shed{crowdedSheds.length > 1 ? 's' : ''} showing high occupancy
              </p>
              <p className="text-xs text-orange-700 mt-1">
                Consider redistributing animals to balance shed capacity
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Statistics */}
      <div className="grid grid-cols-4 gap-3 mb-4">
        <div className="bg-blue-50 p-3 rounded-lg">
          <p className="text-xs text-gray-600">Total Capacity</p>
          <p className="text-lg font-bold text-blue-600">{totalCapacity}</p>
        </div>
        <div className="bg-green-50 p-3 rounded-lg">
          <p className="text-xs text-gray-600">Total Occupancy</p>
          <p className="text-lg font-bold text-green-600">{totalOccupancy}</p>
        </div>
        <div className="bg-purple-50 p-3 rounded-lg">
          <p className="text-xs text-gray-600">Avg Utilization</p>
          <p className="text-lg font-bold text-purple-600">{averageUtilization}%</p>
        </div>
        <div className="bg-indigo-50 p-3 rounded-lg">
          <p className="text-xs text-gray-600">Healthy Sheds</p>
          <p className="text-lg font-bold text-indigo-600">{healthySheds.length}/{data.length}</p>
        </div>
      </div>

      {/* Chart */}
      <ResponsiveContainer width="100%" height={height - 120}>
        <BarChart
          data={chartData}
          margin={{ top: 10, right: 30, left: 10, bottom: 50 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="shedNumber" angle={-45} textAnchor="end" height={70} />
          <YAxis label={{ value: 'Animals', angle: -90, position: 'insideLeft' }} />
          <Tooltip content={<CustomTooltip />} />
          <Legend />
          <Bar dataKey="occupancy" fill="#8b5cf6" radius={[8, 8, 0, 0]} name="Occupancy">
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={getOccupancyColor(entry.occupancy, entry.capacity)} />
            ))}
          </Bar>
          <Bar dataKey="available" fill="#d1d5db" radius={[8, 8, 0, 0]} name="Available Space" />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

// Export memoized component for performance optimization
// This prevents unnecessary re-renders when parent components update
const ShedManagementChart = React.memo(ShedManagementChartContent);

export default ShedManagementChart;
